import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import ExportButton from '@/components/ExportButton';
import { PageHeader } from '@/components/PageHeader';
import { PieChart, Plus, Trash2, Search, FileText, Euro, ArrowUpCircle, ArrowDownCircle, CreditCard, Building, Calculator, TrendingUp, TrendingDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Expense categories with Italian labels
const EXPENSE_CATEGORIES = {
  ENERGIA: 'Energia elettrica',
  ACQUA: 'Acqua',
  GAS: 'Gas',
  RISCALDAMENTO: 'Riscaldamento',
  MANUTENZIONE_ORDINARIA: 'Manutenzione ordinaria',
  MANUTENZIONE_STRAORDINARIA: 'Manutenzione straordinaria',
  PULIZIA: 'Pulizia',
  ASSICURAZIONE: 'Assicurazione',
  AMMINISTRAZIONE: 'Amministrazione',
  COMPENSO_AMMINISTRATORE: 'Compenso amministratore',
  PORTIERE: 'Portiere',
  ASCENSORE: 'Ascensore',
  SPESE_LEGALI: 'Spese legali',
  SPESE_BANCARIE: 'Spese bancarie',
  ALTRO: 'Altro'
};

// Payment methods with Italian labels
const PAYMENT_METHODS = {
  BONIFICO: 'Bonifico bancario',
  ASSEGNO: 'Assegno',
  CONTANTI: 'Contanti',
  RID: 'RID (Addebito diretto)',
  BANCOMAT: 'Bancomat',
  CARTA_CREDITO: 'Carta di credito',
  ALTRO: 'Altro'
};

// Expense status with Italian labels
const EXPENSE_STATUS = {
  DRAFT: { label: 'Bozza', color: 'bg-gray-100 text-gray-800' },
  PENDING: { label: 'Da pagare', color: 'bg-yellow-100 text-yellow-800' },
  APPROVED: { label: 'Approvata', color: 'bg-blue-100 text-blue-800' },
  PAID: { label: 'Pagata', color: 'bg-green-100 text-green-800' },
  CANCELLED: { label: 'Annullata', color: 'bg-red-100 text-red-800' }
};

// Standard Italian rates
const VAT_RATES = [
  { value: 22, label: '22% (Ordinaria)' },
  { value: 10, label: '10% (Ridotta)' },
  { value: 4, label: '4% (Minima)' },
  { value: 0, label: '0% (Esente)' }
];

const WITHHOLDING_RATES = [
  { value: 0, label: 'Nessuna ritenuta' },
  { value: 4, label: '4% (Condomini - pulizie, manutenzione)' },
  { value: 20, label: '20% (Professionisti)' },
  { value: 23, label: '23% (Altro)' }
];

export const Accounting = () => {
  const { token, selectedCondominium } = useAuth();
  const userRole = selectedCondominium?.role || 'RESIDENT';
  const condoId = selectedCondominium?.condominium?.id;
  
  // Data state
  const [suppliers, setSuppliers] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [cashLedger, setCashLedger] = useState([]);
  const [payments, setPayments] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [isExpenseDialogOpen, setIsExpenseDialogOpen] = useState(false);
  const [isLedgerDialogOpen, setIsLedgerDialogOpen] = useState(false);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [isSupplierDialogOpen, setIsSupplierDialogOpen] = useState(false);
  
  // Report state
  const [reportDateFrom, setReportDateFrom] = useState(new Date().getFullYear() + '-01-01');
  const [reportDateTo, setReportDateTo] = useState(new Date().toISOString().split('T')[0]);
  const [vatReport, setVatReport] = useState(null);
  const [withholdingReport, setWithholdingReport] = useState(null);
  const [summary, setSummary] = useState(null);
  
  // Forms
  const [expenseForm, setExpenseForm] = useState({
    supplier_id: '',
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    description: '',
    category: 'ALTRO',
    taxable_amount: '',
    vat_rate: 22,
    withholding_applicable: false,
    withholding_rate: 0,
    withholding_base_rule: 'TAXABLE_ONLY',
    due_date: '',
    payment_date: '',
    payment_method: '',
    status: 'PENDING',
    notes: ''
  });
  
  const [ledgerForm, setLedgerForm] = useState({
    date: new Date().toISOString().split('T')[0],
    entry_type: 'EXPENSE',
    amount: '',
    description: '',
    reference: '',
    category: ''
  });
  
  const [paymentForm, setPaymentForm] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: '',
    method: 'BONIFICO',
    description: '',
    supplier_id: '',
    unit_id: '',
    linked_expense_id: '',
    reference: ''
  });
  
  const [supplierForm, setSupplierForm] = useState({
    name: '',
    vat_number: '',
    fiscal_code: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    province: '',
    iban: '',
    supplier_type: 'COMPANY',
    withholding_applicable: false,
    default_vat_rate: 22,
    default_withholding_rate: 0,
    withholding_category: 'OTHER',
    notes: ''
  });

  // Fetch functions
  const fetchSuppliers = useCallback(async () => {
    if (!condoId) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/suppliers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setSuppliers(await res.json());
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  }, [condoId, token]);

  const fetchExpenses = useCallback(async () => {
    if (!condoId) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/expenses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setExpenses(await res.json());
    } catch (error) {
      console.error('Error fetching expenses:', error);
    }
  }, [condoId, token]);

  const fetchCashLedger = useCallback(async () => {
    if (!condoId) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/cash-ledger`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setCashLedger(await res.json());
    } catch (error) {
      console.error('Error fetching cash ledger:', error);
    }
  }, [condoId, token]);

  const fetchPayments = useCallback(async () => {
    if (!condoId) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setPayments(await res.json());
    } catch (error) {
      console.error('Error fetching payments:', error);
    }
  }, [condoId, token]);

  const fetchUnits = useCallback(async () => {
    if (!condoId) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/units`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setUnits(await res.json());
    } catch (error) {
      console.error('Error fetching units:', error);
    }
  }, [condoId, token]);

  const fetchSummary = useCallback(async () => {
    if (!condoId) return;
    try {
      const year = new Date().getFullYear();
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/reports/summary?year=${year}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setSummary(await res.json());
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  }, [condoId, token]);

  const fetchVatReport = async () => {
    if (!condoId) return;
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/reports/vat?from_date=${reportDateFrom}&to_date=${reportDateTo}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) setVatReport(await res.json());
    } catch (error) {
      toast.error('Errore nel caricamento report IVA');
    }
  };

  const fetchWithholdingReport = async () => {
    if (!condoId) return;
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/reports/withholding?from_date=${reportDateFrom}&to_date=${reportDateTo}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) setWithholdingReport(await res.json());
    } catch (error) {
      toast.error('Errore nel caricamento report ritenute');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchSuppliers(),
        fetchExpenses(),
        fetchCashLedger(),
        fetchPayments(),
        fetchUnits(),
        fetchSummary()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchSuppliers, fetchExpenses, fetchCashLedger, fetchPayments, fetchUnits, fetchSummary]);

  // Calculate expense amounts
  const calculateExpenseAmounts = (taxable, vatRate, withholdingRate, withholdingApplicable = false, baseRule = 'TAXABLE_ONLY') => {
    const t = parseFloat(taxable) || 0;
    const vat = Math.round(t * vatRate / 100 * 100) / 100;
    const total = Math.round((t + vat) * 100) / 100;
    
    // Calculate withholding base
    let withholdingBase = t;
    if (baseRule === 'GROSS_TOTAL') {
      withholdingBase = total;
    }
    
    const withholding = (withholdingApplicable && withholdingRate > 0) 
      ? Math.round(withholdingBase * withholdingRate / 100 * 100) / 100 
      : 0;
    const netToPay = Math.round((total - withholding) * 100) / 100;
    return { vat, total, withholding, netToPay, withholdingBase };
  };

  // Create expense
  const handleCreateExpense = async (e) => {
    e.preventDefault();
    try {
      const calculated = calculateExpenseAmounts(
        expenseForm.taxable_amount,
        expenseForm.vat_rate,
        expenseForm.withholding_rate,
        expenseForm.withholding_applicable,
        expenseForm.withholding_base_rule
      );
      
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/expenses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...expenseForm,
          taxable_amount: parseFloat(expenseForm.taxable_amount),
          vat_amount: calculated.vat,
          total: calculated.total,
          withholding_base_amount: calculated.withholdingBase,
          withholding_amount: calculated.withholding,
          net_to_pay: calculated.netToPay
        })
      });
      
      if (res.ok) {
        toast.success('Spesa registrata con successo');
        setIsExpenseDialogOpen(false);
        resetExpenseForm();
        fetchExpenses();
        fetchCashLedger();
        fetchSummary();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore nella registrazione');
      }
    } catch (error) {
      toast.error('Errore nella registrazione');
    }
  };

  // Create cash ledger entry
  const handleCreateLedgerEntry = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/cash-ledger`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...ledgerForm,
          amount: parseFloat(ledgerForm.amount)
        })
      });
      
      if (res.ok) {
        toast.success('Movimento registrato');
        setIsLedgerDialogOpen(false);
        resetLedgerForm();
        fetchCashLedger();
        fetchSummary();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore nella registrazione');
      }
    } catch (error) {
      toast.error('Errore nella registrazione');
    }
  };

  // Create payment
  const handleCreatePayment = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/payments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...paymentForm,
          amount: parseFloat(paymentForm.amount),
          supplier_id: paymentForm.supplier_id || null,
          unit_id: paymentForm.unit_id || null,
          linked_expense_id: paymentForm.linked_expense_id || null
        })
      });
      
      if (res.ok) {
        toast.success('Pagamento registrato');
        setIsPaymentDialogOpen(false);
        resetPaymentForm();
        fetchPayments();
        fetchExpenses();
        fetchCashLedger();
        fetchSummary();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore nella registrazione');
      }
    } catch (error) {
      toast.error('Errore nella registrazione');
    }
  };

  // Create supplier
  const handleCreateSupplier = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/suppliers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(supplierForm)
      });
      
      if (res.ok) {
        toast.success('Fornitore creato');
        setIsSupplierDialogOpen(false);
        resetSupplierForm();
        fetchSuppliers();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore nella creazione');
      }
    } catch (error) {
      toast.error('Errore nella creazione');
    }
  };

  // Delete functions
  const handleDeleteExpense = async (id) => {
    if (!window.confirm('Eliminare questa spesa?')) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/expenses/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Spesa eliminata');
        fetchExpenses();
        fetchSummary();
      }
    } catch (error) {
      toast.error('Errore eliminazione');
    }
  };

  const handleDeleteLedgerEntry = async (id) => {
    if (!window.confirm('Eliminare questo movimento?')) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/cash-ledger/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Movimento eliminato');
        fetchCashLedger();
        fetchSummary();
      }
    } catch (error) {
      toast.error('Errore eliminazione');
    }
  };

  const handleDeletePayment = async (id) => {
    if (!window.confirm('Eliminare questo pagamento?')) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/payments/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Pagamento eliminato');
        fetchPayments();
        fetchSummary();
      }
    } catch (error) {
      toast.error('Errore eliminazione');
    }
  };

  // Reset forms
  const resetExpenseForm = () => {
    setExpenseForm({
      supplier_id: '',
      invoice_number: '',
      invoice_date: new Date().toISOString().split('T')[0],
      description: '',
      category: 'ALTRO',
      taxable_amount: '',
      vat_rate: 22,
      withholding_rate: 0,
      due_date: '',
      status: 'PENDING',
      notes: ''
    });
  };

  const resetLedgerForm = () => {
    setLedgerForm({
      date: new Date().toISOString().split('T')[0],
      entry_type: 'EXPENSE',
      amount: '',
      description: '',
      reference: '',
      category: ''
    });
  };

  const resetPaymentForm = () => {
    setPaymentForm({
      date: new Date().toISOString().split('T')[0],
      amount: '',
      method: 'BONIFICO',
      description: '',
      supplier_id: '',
      unit_id: '',
      linked_expense_id: '',
      reference: ''
    });
  };

  const resetSupplierForm = () => {
    setSupplierForm({
      name: '',
      vat_number: '',
      fiscal_code: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      postal_code: '',
      iban: '',
      default_vat_rate: 22,
      default_withholding_rate: 0,
      notes: ''
    });
  };

  // Format helpers
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT');
  };

  // Calculate live totals for expense form
  const calculatedAmounts = calculateExpenseAmounts(
    expenseForm.taxable_amount,
    expenseForm.vat_rate,
    expenseForm.withholding_rate,
    expenseForm.withholding_applicable,
    expenseForm.withholding_base_rule
  );

  const canEdit = userRole === 'ADMIN';
  const canView = userRole === 'ADMIN' || userRole === 'ACCOUNTANT';

  if (!canView) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <PieChart className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">Accesso non autorizzato</h3>
            <p className="text-muted-foreground">
              Solo amministratori e contabili possono accedere alla contabilità.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Contabilità"
        subtitle="Movimenti e prima nota"
        actions={
          <>
            <ExportButton
              condominiumId={condoId}
              exportType="vat-report"
              title="Report IVA"
              formats={['pdf', 'csv']}
              showDateRange={true}
              showArchive={true}
              token={token}
            />
            <ExportButton
              condominiumId={condoId}
              exportType="withholding-report"
              title="Ritenute"
              formats={['pdf']}
              showDateRange={true}
              showArchive={true}
              token={token}
            />
          </>
        }
      />

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Entrate {summary.year}</p>
                  <p className="text-xl font-bold text-green-600">{formatCurrency(summary.income?.total)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <TrendingDown className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Uscite {summary.year}</p>
                  <p className="text-xl font-bold text-red-600">{formatCurrency(summary.expenses?.total)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Euro className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Saldo</p>
                  <p className={`text-xl font-bold ${summary.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(summary.balance)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <FileText className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Da pagare</p>
                  <p className="text-xl font-bold text-yellow-600">{summary.expenses?.pending || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Tabs */}
      <Tabs defaultValue="ledger" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="ledger">Prima Nota</TabsTrigger>
          <TabsTrigger value="expenses">Spese/Fatture</TabsTrigger>
          <TabsTrigger value="payments">Pagamenti</TabsTrigger>
          <TabsTrigger value="reports">Report</TabsTrigger>
        </TabsList>

        {/* Prima Nota Tab */}
        <TabsContent value="ledger" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Prima Nota</h2>
            {canEdit && (
              <Dialog open={isLedgerDialogOpen} onOpenChange={setIsLedgerDialogOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="mr-2 h-4 w-4" />
                    Nuovo Movimento
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Nuovo Movimento</DialogTitle>
                    <DialogDescription>Registra un movimento in prima nota</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateLedgerEntry} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Data *</Label>
                        <Input
                          type="date"
                          value={ledgerForm.date}
                          onChange={(e) => setLedgerForm({ ...ledgerForm, date: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Tipo *</Label>
                        <Select
                          value={ledgerForm.entry_type}
                          onValueChange={(v) => setLedgerForm({ ...ledgerForm, entry_type: v })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="INCOME">Entrata</SelectItem>
                            <SelectItem value="EXPENSE">Uscita</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Importo *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={ledgerForm.amount}
                        onChange={(e) => setLedgerForm({ ...ledgerForm, amount: e.target.value })}
                        placeholder="0.00"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Descrizione *</Label>
                      <Input
                        value={ledgerForm.description}
                        onChange={(e) => setLedgerForm({ ...ledgerForm, description: e.target.value })}
                        placeholder="Descrizione movimento"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Riferimento</Label>
                        <Input
                          value={ledgerForm.reference}
                          onChange={(e) => setLedgerForm({ ...ledgerForm, reference: e.target.value })}
                          placeholder="N. fattura, bonifico..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Categoria</Label>
                        <Select
                          value={ledgerForm.category}
                          onValueChange={(v) => setLedgerForm({ ...ledgerForm, category: v })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Seleziona..." />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(EXPENSE_CATEGORIES).map(([k, v]) => (
                              <SelectItem key={k} value={k}>{v}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setIsLedgerDialogOpen(false)}>
                        Annulla
                      </Button>
                      <Button type="submit">Salva</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>

          <Card>
            <CardContent className="pt-6">
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Caricamento...</div>
              ) : cashLedger.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">Nessun movimento registrato</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Descrizione</TableHead>
                      <TableHead>Riferimento</TableHead>
                      <TableHead className="text-right">Importo</TableHead>
                      {canEdit && <TableHead className="w-16"></TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cashLedger.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell>{formatDate(entry.date)}</TableCell>
                        <TableCell>
                          <Badge className={entry.entry_type === 'INCOME' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                            {entry.entry_type === 'INCOME' ? (
                              <><ArrowUpCircle className="h-3 w-3 mr-1" /> Entrata</>
                            ) : (
                              <><ArrowDownCircle className="h-3 w-3 mr-1" /> Uscita</>
                            )}
                          </Badge>
                        </TableCell>
                        <TableCell>{entry.description}</TableCell>
                        <TableCell className="text-muted-foreground">{entry.reference || '-'}</TableCell>
                        <TableCell className={`text-right font-medium ${entry.entry_type === 'INCOME' ? 'text-green-600' : 'text-red-600'}`}>
                          {entry.entry_type === 'INCOME' ? '+' : '-'}{formatCurrency(entry.amount)}
                        </TableCell>
                        {canEdit && (
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteLedgerEntry(entry.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Spese/Fatture Tab */}
        <TabsContent value="expenses" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Spese e Fatture</h2>
            <div className="flex gap-2">
              {canEdit && (
                <>
                  <Dialog open={isSupplierDialogOpen} onOpenChange={setIsSupplierDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline">
                        <Building className="mr-2 h-4 w-4" />
                        Nuovo Fornitore
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg">
                      <DialogHeader>
                        <DialogTitle>Nuovo Fornitore</DialogTitle>
                        <DialogDescription>Aggiungi un nuovo fornitore all'anagrafica</DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleCreateSupplier} className="space-y-4">
                        <div className="space-y-2">
                          <Label>Ragione Sociale *</Label>
                          <Input
                            value={supplierForm.name}
                            onChange={(e) => setSupplierForm({ ...supplierForm, name: e.target.value })}
                            required
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Partita IVA</Label>
                            <Input
                              value={supplierForm.vat_number}
                              onChange={(e) => setSupplierForm({ ...supplierForm, vat_number: e.target.value })}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Codice Fiscale</Label>
                            <Input
                              value={supplierForm.fiscal_code}
                              onChange={(e) => setSupplierForm({ ...supplierForm, fiscal_code: e.target.value })}
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Email</Label>
                            <Input
                              type="email"
                              value={supplierForm.email}
                              onChange={(e) => setSupplierForm({ ...supplierForm, email: e.target.value })}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Telefono</Label>
                            <Input
                              value={supplierForm.phone}
                              onChange={(e) => setSupplierForm({ ...supplierForm, phone: e.target.value })}
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>IBAN</Label>
                          <Input
                            value={supplierForm.iban}
                            onChange={(e) => setSupplierForm({ ...supplierForm, iban: e.target.value })}
                            placeholder="IT..."
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Aliquota IVA default</Label>
                            <Select
                              value={String(supplierForm.default_vat_rate)}
                              onValueChange={(v) => setSupplierForm({ ...supplierForm, default_vat_rate: parseFloat(v) })}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {VAT_RATES.map((r) => (
                                  <SelectItem key={r.value} value={String(r.value)}>{r.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>Ritenuta default</Label>
                            <div className="flex items-center gap-4">
                              <Switch
                                checked={supplierForm.withholding_applicable}
                                onCheckedChange={(checked) => {
                                  setSupplierForm({ 
                                    ...supplierForm, 
                                    withholding_applicable: checked,
                                    default_withholding_rate: checked ? (supplierForm.default_withholding_rate || 4) : 0
                                  });
                                }}
                              />
                              <span className="text-sm">{supplierForm.withholding_applicable ? 'Applica ritenuta' : 'Nessuna ritenuta'}</span>
                            </div>
                            {supplierForm.withholding_applicable && (
                              <Select
                                value={String(supplierForm.default_withholding_rate)}
                                onValueChange={(v) => setSupplierForm({ ...supplierForm, default_withholding_rate: parseFloat(v) })}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {WITHHOLDING_RATES.filter(r => r.value > 0).map((r) => (
                                    <SelectItem key={r.value} value={String(r.value)}>{r.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                        </div>
                        <DialogFooter>
                          <Button type="button" variant="outline" onClick={() => setIsSupplierDialogOpen(false)}>
                            Annulla
                          </Button>
                          <Button type="submit">Salva</Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>

                  <Dialog open={isExpenseDialogOpen} onOpenChange={setIsExpenseDialogOpen}>
                    <DialogTrigger asChild>
                      <Button>
                        <Plus className="mr-2 h-4 w-4" />
                        Nuova Spesa
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Registra Spesa/Fattura</DialogTitle>
                        <DialogDescription>
                          Registra una nuova fattura fornitore con calcolo automatico IVA e ritenuta
                        </DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleCreateExpense} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Fornitore</Label>
                            <Select
                              value={expenseForm.supplier_id}
                              onValueChange={(v) => {
                                const supplier = suppliers.find(s => s.id === v);
                                const hasWithholding = supplier?.withholding_applicable || (supplier?.default_withholding_rate > 0);
                                setExpenseForm({
                                  ...expenseForm,
                                  supplier_id: v,
                                  vat_rate: supplier?.default_vat_rate || 22,
                                  withholding_applicable: hasWithholding,
                                  withholding_rate: hasWithholding ? (supplier?.default_withholding_rate || 4) : 0
                                });
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Seleziona fornitore..." />
                              </SelectTrigger>
                              <SelectContent>
                                {suppliers.map((s) => (
                                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>Categoria</Label>
                            <Select
                              value={expenseForm.category}
                              onValueChange={(v) => setExpenseForm({ ...expenseForm, category: v })}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(EXPENSE_CATEGORIES).map(([k, v]) => (
                                  <SelectItem key={k} value={k}>{v}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label>N. Fattura</Label>
                            <Input
                              value={expenseForm.invoice_number}
                              onChange={(e) => setExpenseForm({ ...expenseForm, invoice_number: e.target.value })}
                              placeholder="Es: 2024/001"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Data Fattura *</Label>
                            <Input
                              type="date"
                              value={expenseForm.invoice_date}
                              onChange={(e) => setExpenseForm({ ...expenseForm, invoice_date: e.target.value })}
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Scadenza</Label>
                            <Input
                              type="date"
                              value={expenseForm.due_date}
                              onChange={(e) => setExpenseForm({ ...expenseForm, due_date: e.target.value })}
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label>Descrizione *</Label>
                          <Textarea
                            value={expenseForm.description}
                            onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                            placeholder="Descrizione della spesa..."
                            required
                          />
                        </div>

                        {/* Financial calculation section */}
                        <Card className="bg-slate-50">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm flex items-center gap-2">
                              <Calculator className="h-4 w-4" />
                              Calcolo Automatico
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            <div className="grid grid-cols-3 gap-4">
                              <div className="space-y-2">
                                <Label>Imponibile *</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={expenseForm.taxable_amount}
                                  onChange={(e) => setExpenseForm({ ...expenseForm, taxable_amount: e.target.value })}
                                  placeholder="0.00"
                                  required
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>Aliquota IVA</Label>
                                <Select
                                  value={String(expenseForm.vat_rate)}
                                  onValueChange={(v) => setExpenseForm({ ...expenseForm, vat_rate: parseFloat(v) })}
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {VAT_RATES.map((r) => (
                                      <SelectItem key={r.value} value={String(r.value)}>{r.label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-2">
                                <Label>Ritenuta d'acconto</Label>
                                <div className="flex items-center gap-4">
                                  <Switch
                                    checked={expenseForm.withholding_applicable}
                                    onCheckedChange={(checked) => {
                                      setExpenseForm({ 
                                        ...expenseForm, 
                                        withholding_applicable: checked,
                                        withholding_rate: checked ? (expenseForm.withholding_rate || 4) : 0
                                      });
                                    }}
                                  />
                                  <span className="text-sm">{expenseForm.withholding_applicable ? 'Applica ritenuta' : 'Nessuna ritenuta'}</span>
                                </div>
                                {expenseForm.withholding_applicable && (
                                  <Select
                                    value={String(expenseForm.withholding_rate)}
                                    onValueChange={(v) => setExpenseForm({ ...expenseForm, withholding_rate: parseFloat(v) })}
                                  >
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {WITHHOLDING_RATES.filter(r => r.value > 0).map((r) => (
                                        <SelectItem key={r.value} value={String(r.value)}>{r.label}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                )}
                              </div>
                            </div>

                            {/* Calculated totals display */}
                            <div className="grid grid-cols-4 gap-4 pt-4 border-t">
                              <div className="text-center">
                                <p className="text-xs text-muted-foreground">IVA</p>
                                <p className="font-medium">{formatCurrency(calculatedAmounts.vat)}</p>
                              </div>
                              <div className="text-center">
                                <p className="text-xs text-muted-foreground">Totale Fattura</p>
                                <p className="font-medium">{formatCurrency(calculatedAmounts.total)}</p>
                              </div>
                              <div className="text-center">
                                <p className="text-xs text-muted-foreground">Ritenuta</p>
                                <p className={`font-medium ${calculatedAmounts.withholding > 0 ? 'text-orange-600' : 'text-muted-foreground'}`}>
                                  {formatCurrency(calculatedAmounts.withholding)}
                                </p>
                              </div>
                              <div className="text-center bg-green-50 p-2 rounded">
                                <p className="text-xs text-muted-foreground">Netto da Pagare</p>
                                <p className="font-bold text-green-600">{formatCurrency(calculatedAmounts.netToPay)}</p>
                              </div>
                            </div>

                            {/* Info: ritenuta auto-registrata */}
                            {expenseForm.withholding_applicable && (
                              <div className="text-xs text-muted-foreground bg-blue-50 p-2 rounded">
                                💡 La ritenuta sarà automaticamente registrata nel registro ritenute quando la fattura viene approvata.
                              </div>
                            )}
                          </CardContent>
                        </Card>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Stato</Label>
                            <Select
                              value={expenseForm.status}
                              onValueChange={(v) => setExpenseForm({ ...expenseForm, status: v })}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="DRAFT">Bozza</SelectItem>
                                <SelectItem value="PENDING">Da pagare</SelectItem>
                                <SelectItem value="APPROVED">Approvata</SelectItem>
                                <SelectItem value="PAID">Pagata</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label>Note</Label>
                          <Textarea
                            value={expenseForm.notes}
                            onChange={(e) => setExpenseForm({ ...expenseForm, notes: e.target.value })}
                            placeholder="Note aggiuntive..."
                          />
                        </div>

                        <DialogFooter>
                          <Button type="button" variant="outline" onClick={() => setIsExpenseDialogOpen(false)}>
                            Annulla
                          </Button>
                          <Button type="submit">Registra Spesa</Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                </>
              )}
            </div>
          </div>

          <Card>
            <CardContent className="pt-6">
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Caricamento...</div>
              ) : expenses.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">Nessuna spesa registrata</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Fornitore</TableHead>
                      <TableHead>Descrizione</TableHead>
                      <TableHead>Categoria</TableHead>
                      <TableHead className="text-right">Imponibile</TableHead>
                      <TableHead className="text-right">IVA</TableHead>
                      <TableHead className="text-right">Totale</TableHead>
                      <TableHead className="text-right">Ritenuta</TableHead>
                      <TableHead className="text-right">Netto</TableHead>
                      <TableHead>Stato</TableHead>
                      {canEdit && <TableHead className="w-16"></TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {expenses.map((exp) => {
                      const statusInfo = EXPENSE_STATUS[exp.status] || EXPENSE_STATUS.PENDING;
                      return (
                        <TableRow key={exp.id}>
                          <TableCell>{formatDate(exp.invoice_date)}</TableCell>
                          <TableCell className="font-medium">{exp.supplier_name || '-'}</TableCell>
                          <TableCell className="max-w-[200px] truncate">{exp.description}</TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {EXPENSE_CATEGORIES[exp.category] || exp.category}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">{formatCurrency(exp.taxable_amount)}</TableCell>
                          <TableCell className="text-right text-muted-foreground">{formatCurrency(exp.vat_amount)}</TableCell>
                          <TableCell className="text-right font-medium">{formatCurrency(exp.total)}</TableCell>
                          <TableCell className="text-right text-orange-600">{formatCurrency(exp.withholding_amount)}</TableCell>
                          <TableCell className="text-right font-bold text-green-600">{formatCurrency(exp.net_to_pay)}</TableCell>
                          <TableCell>
                            <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
                          </TableCell>
                          {canEdit && (
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteExpense(exp.id)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pagamenti Tab */}
        <TabsContent value="payments" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Pagamenti</h2>
            {canEdit && (
              <Dialog open={isPaymentDialogOpen} onOpenChange={setIsPaymentDialogOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="mr-2 h-4 w-4" />
                    Nuovo Pagamento
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Registra Pagamento</DialogTitle>
                    <DialogDescription>Registra un pagamento effettuato o ricevuto</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreatePayment} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Data *</Label>
                        <Input
                          type="date"
                          value={paymentForm.date}
                          onChange={(e) => setPaymentForm({ ...paymentForm, date: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Importo *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={paymentForm.amount}
                          onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                          placeholder="0.00"
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Metodo di Pagamento</Label>
                      <Select
                        value={paymentForm.method}
                        onValueChange={(v) => setPaymentForm({ ...paymentForm, method: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(PAYMENT_METHODS).map(([k, v]) => (
                            <SelectItem key={k} value={k}>{v}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Descrizione</Label>
                      <Input
                        value={paymentForm.description}
                        onChange={(e) => setPaymentForm({ ...paymentForm, description: e.target.value })}
                        placeholder="Descrizione pagamento"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Fornitore (se uscita)</Label>
                        <Select
                          value={paymentForm.supplier_id}
                          onValueChange={(v) => setPaymentForm({ ...paymentForm, supplier_id: v, unit_id: '' })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Seleziona..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="">Nessuno</SelectItem>
                            {suppliers.map((s) => (
                              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Unità (se entrata)</Label>
                        <Select
                          value={paymentForm.unit_id}
                          onValueChange={(v) => setPaymentForm({ ...paymentForm, unit_id: v, supplier_id: '' })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Seleziona..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="">Nessuna</SelectItem>
                            {units.map((u) => (
                              <SelectItem key={u.id} value={u.id}>{u.unit_number}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Collega a Fattura</Label>
                        <Select
                          value={paymentForm.linked_expense_id}
                          onValueChange={(v) => setPaymentForm({ ...paymentForm, linked_expense_id: v })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Seleziona..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="">Nessuna</SelectItem>
                            {expenses.filter(e => e.status !== 'PAID').map((e) => (
                              <SelectItem key={e.id} value={e.id}>
                                {e.invoice_number || 'N/A'} - {formatCurrency(e.net_to_pay)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Riferimento</Label>
                        <Input
                          value={paymentForm.reference}
                          onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
                          placeholder="CRO, n. assegno..."
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setIsPaymentDialogOpen(false)}>
                        Annulla
                      </Button>
                      <Button type="submit">Registra</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>

          <Card>
            <CardContent className="pt-6">
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Caricamento...</div>
              ) : payments.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">Nessun pagamento registrato</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Descrizione</TableHead>
                      <TableHead>Fornitore/Unità</TableHead>
                      <TableHead>Metodo</TableHead>
                      <TableHead>Riferimento</TableHead>
                      <TableHead className="text-right">Importo</TableHead>
                      {canEdit && <TableHead className="w-16"></TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.map((pmt) => (
                      <TableRow key={pmt.id}>
                        <TableCell>{formatDate(pmt.date)}</TableCell>
                        <TableCell>{pmt.description || '-'}</TableCell>
                        <TableCell>
                          {pmt.supplier_name ? (
                            <div className="flex items-center gap-1">
                              <Building className="h-3 w-3" />
                              {pmt.supplier_name}
                            </div>
                          ) : pmt.unit_number ? (
                            <div className="flex items-center gap-1">
                              <ArrowUpCircle className="h-3 w-3 text-green-600" />
                              Unità {pmt.unit_number}
                            </div>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            <CreditCard className="h-3 w-3 mr-1" />
                            {PAYMENT_METHODS[pmt.method] || pmt.method}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{pmt.reference || '-'}</TableCell>
                        <TableCell className={`text-right font-medium ${pmt.unit_id ? 'text-green-600' : 'text-red-600'}`}>
                          {pmt.unit_id ? '+' : '-'}{formatCurrency(pmt.amount)}
                        </TableCell>
                        {canEdit && (
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeletePayment(pmt.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Report Tab */}
        <TabsContent value="reports" className="space-y-4">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <h2 className="text-lg font-semibold">Report Fiscali</h2>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Label className="whitespace-nowrap">Dal:</Label>
                <Input
                  type="date"
                  value={reportDateFrom}
                  onChange={(e) => setReportDateFrom(e.target.value)}
                  className="w-40"
                />
              </div>
              <div className="flex items-center gap-2">
                <Label className="whitespace-nowrap">Al:</Label>
                <Input
                  type="date"
                  value={reportDateTo}
                  onChange={(e) => setReportDateTo(e.target.value)}
                  className="w-40"
                />
              </div>
              <Button onClick={() => { fetchVatReport(); fetchWithholdingReport(); }}>
                <Search className="mr-2 h-4 w-4" />
                Genera Report
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* VAT Report */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Report IVA
                </CardTitle>
                <CardDescription>Riepilogo IVA per aliquota e fornitore</CardDescription>
              </CardHeader>
              <CardContent>
                {!vatReport ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Clicca "Genera Report" per visualizzare i dati
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* By Rate */}
                    <div>
                      <h4 className="font-medium mb-2">Per Aliquota</h4>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Aliquota</TableHead>
                            <TableHead className="text-right">Imponibile</TableHead>
                            <TableHead className="text-right">IVA</TableHead>
                            <TableHead className="text-right">N. Fatt.</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {vatReport.by_vat_rate?.map((r) => (
                            <TableRow key={r.rate}>
                              <TableCell>{r.rate}%</TableCell>
                              <TableCell className="text-right">{formatCurrency(r.taxable)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(r.vat)}</TableCell>
                              <TableCell className="text-right">{r.count}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Totals */}
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <p className="text-sm text-muted-foreground">Totale Imponibile</p>
                          <p className="font-bold">{formatCurrency(vatReport.totals?.taxable)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Totale IVA</p>
                          <p className="font-bold text-blue-600">{formatCurrency(vatReport.totals?.vat)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">N. Fatture</p>
                          <p className="font-bold">{vatReport.totals?.count}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Withholding Report */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="h-5 w-5" />
                  Report Ritenute d'Acconto
                </CardTitle>
                <CardDescription>Riepilogo ritenute per il periodo</CardDescription>
              </CardHeader>
              <CardContent>
                {!withholdingReport ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Clicca "Genera Report" per visualizzare i dati
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* By Rate */}
                    <div>
                      <h4 className="font-medium mb-2">Per Aliquota</h4>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Aliquota</TableHead>
                            <TableHead className="text-right">Imponibile</TableHead>
                            <TableHead className="text-right">Ritenuta</TableHead>
                            <TableHead className="text-right">N. Fatt.</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {withholdingReport.by_rate?.map((r) => (
                            <TableRow key={r.rate}>
                              <TableCell>{r.rate}%</TableCell>
                              <TableCell className="text-right">{formatCurrency(r.taxable)}</TableCell>
                              <TableCell className="text-right">{formatCurrency(r.withholding)}</TableCell>
                              <TableCell className="text-right">{r.count}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Totals */}
                    <div className="bg-orange-50 p-4 rounded-lg">
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <p className="text-sm text-muted-foreground">Totale Imponibile</p>
                          <p className="font-bold">{formatCurrency(withholdingReport.totals?.taxable)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Totale Ritenute</p>
                          <p className="font-bold text-orange-600">{formatCurrency(withholdingReport.totals?.withholding)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">N. Fatture</p>
                          <p className="font-bold">{withholdingReport.totals?.count}</p>
                        </div>
                      </div>
                    </div>

                    {/* Details */}
                    {withholdingReport.details?.length > 0 && (
                      <div>
                        <h4 className="font-medium mb-2">Dettaglio Fatture</h4>
                        <div className="max-h-60 overflow-y-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Data</TableHead>
                                <TableHead>N. Fatt.</TableHead>
                                <TableHead>Fornitore</TableHead>
                                <TableHead className="text-right">Ritenuta</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {withholdingReport.details.map((d, i) => (
                                <TableRow key={i}>
                                  <TableCell>{formatDate(d.invoice_date)}</TableCell>
                                  <TableCell>{d.invoice_number || '-'}</TableCell>
                                  <TableCell>{d.supplier || '-'}</TableCell>
                                  <TableCell className="text-right text-orange-600">
                                    {formatCurrency(d.withholding)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};
